// src/pages/EditPaymentPage.js - Rebuilt to match CreatePaymentPage architecture
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, MenuItem, Stack, Button,
  Avatar, Paper, CircularProgress
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchPaymentById, updatePayment } from '../slices/financeSlice';
import { useToast } from '../context/ToastContext';
import { fetchBuses } from '../slices/busesSlice';
import { fetchStaffMinimal } from '../slices/staffSlice';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import { fetchAuthorizedPayers } from '../slices/authorizedPayersSlice';
import BankBranchSelect from '../components/BankBranchSelect';
import EntitySelect from '../components/EntitySelect';

const MotionPaper = motion(Paper);

// Shared field styling for the design system
const fieldSx = {
  bgcolor: '#fff',
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    fontFamily: 'Cairo, Tajawal, sans-serif',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#4338CA' },
    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Cairo, Tajawal, sans-serif' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
};

const paymentTypes = [
  { id: 'uuid-1', name: 'cash', display_name: 'نقداً', type: 'cash' },
  { id: 'uuid-2', name: 'bank_transfer', display_name: 'تحويل بنكي', type: 'bank_transfer' },
  { id: 'uuid-3', name: 'cheque', display_name: 'شيك', type: 'cheque' },
  { id: 'uuid-4', name: 'visa', display_name: 'فيزا', type: 'visa' },
];

const entityTypes = ['موظف', 'باص', 'جهة صرف معتمدة', 'متفرقات'];

const EditPaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const payment = useSelector((state) => state.finance.selectedPayment);
  const authorizedPayers = useSelector((state) => state.authorizedPayers.items);
  const { minimal: staffMinimal, minimalStatus } = useSelector((state) => state.staff);
  const buses = useSelector((state) => state.buses.list);

  const [isUpdating, setIsUpdating] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [form, setForm] = useState(null);

  // Fetch payment and supporting data
  useEffect(() => {
    dispatch(fetchPaymentById(id));
    dispatch(fetchStaffMinimal({ is_archived: false }));
    dispatch(fetchBuses());
    dispatch(fetchSchoolYears());
    dispatch(fetchAuthorizedPayers());
  }, [id, dispatch]);

  // Populate form when payment data loads
  useEffect(() => {
    if (payment) {
      setForm({
        receiptNumber: payment.receive_id || '',
        entityType: payment.type || '',
        targetName: payment.target_name || payment.targetName || '',
        targetId: payment.recipient_employee || payment.recipient_bus || payment.recipient_authorized || '',
        method: payment.payment_type || '',
        amount: payment.amount || '',
        paymentDate: payment.date || new Date().toISOString().split('T')[0],
        reason: payment.reason || '',
        school_year: payment.school_year || '',
        chequeDetails: {
          chequeNumber: payment.cheque?.cheque_number || '',
          dueDate: payment.cheque?.cheque_date || '',
          bankNumber: payment.cheque?.bank_number || '',
          branchNumber: payment.cheque?.branch_number || '',
          bankName: payment.cheque?.bank_name || '',
          branchName: payment.cheque?.branch_name || '',
          countryCode: payment.cheque?.country_code || '',
          // Reference ids aren't persisted (only name/number snapshots), so on
          // edit the select starts unresolved — user can re-pick from the list
          // or keep the existing name/number as-is.
          bankId: null,
          branchId: null,
          accountNumber: payment.cheque?.account_number || '',
          description: payment.cheque?.description || '',
          chequeImage: null,
        },
        visaDetails: {
          receiptNumber: payment.visa?.receipt_number || '',
          notes: payment.visa?.description || '',
        },
      });
    }
  }, [payment]);

  // Entity options matching CreatePaymentPage
  const entityOptions = {
    موظف: staffMinimal.filter(s => !s.is_archived).map((emp) => ({
      id: emp.id,
      name: emp.full_name,
      employee_id: emp.employee_id,
    })),
    باص: buses.map((bus) => ({
      id: bus.id,
      name: bus.name || bus.bus_number || 'باص غير معرف',
    })),
    'جهة صرف معتمدة': authorizedPayers.map((payer) => ({
      id: payer.id,
      name: payer.display_value || payer.name,
    })),
    متفرقات: [
      { id: 'misc1', name: 'شاي' },
      { id: 'misc2', name: 'قهوة' },
      { id: 'misc3', name: 'مستلزمات أخرى' },
    ],
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
      setForm((prev) => ({
        ...prev,
        chequeDetails: {
          ...prev.chequeDetails,
          chequeImage: file,
        },
      }));
    }
  };

  const handleChequeChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      chequeDetails: {
        ...prev.chequeDetails,
        [name]: value,
      },
    }));
  };

  const handleVisaChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      visaDetails: {
        ...prev.visaDetails,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.amount || parseFloat(form.amount) <= 0) {
      showToast('يرجى إدخال مبلغ صحيح', 'error');
      return;
    }

    setIsUpdating(true);

    const paymentData = {
      id: id,
      ...form,
      chequeDetails: form.method === 'شيك' ? form.chequeDetails : {},
      visaDetails: form.method === 'فيزا' ? form.visaDetails : {},
      proofAttachment: attachment || null,
    };

    try {
      const resultAction = await dispatch(updatePayment(paymentData));
      if (updatePayment.rejected.match(resultAction)) {
        throw resultAction.error;
      }
      showToast('تم تحديث بيانات الدفعة بنجاح!', 'success');
      navigate('/financial');
    } catch (err) {
      console.error('Payment update error:', err);
      showToast('حدث خطأ أثناء تحديث السند', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!payment || !form) {
    return (
      <Box
        sx={{
          direction: 'rtl',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
        }}
      >
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  const selectedEmployee = form.entityType === 'موظف'
    ? entityOptions.موظف.find(emp => emp.id === form.targetId)
    : null;

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EditNoteIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                تعديل سند الصرف
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                تحديث بيانات سند الصرف المالي
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/financial')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            العودة للقائمة
          </Button>
        </MotionPaper>

        <form onSubmit={handleSubmit}>
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              mb: 3,
            }}
          >
            <Stack spacing={3} dir="rtl">
              <TextField
                label="نوع الجهة"
                name="entityType"
                value={form.entityType}
                onChange={handleChange}
                select
                fullWidth
                sx={fieldSx}
              >
                {entityTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>

              {/* Entity Selection with Search */}
              {form.entityType && (
                <EntitySelect
                  entityType={form.entityType}
                  options={entityOptions[form.entityType]}
                  targetId={form.targetId}
                  loading={form.entityType === 'موظف' && minimalStatus === 'loading'}
                  onChange={(entity) => {
                    setForm((prev) => ({
                      ...prev,
                      targetName: entity?.name || '',
                      targetId: entity?.id || '',
                    }));
                  }}
                  fieldSx={fieldSx}
                />
              )}

              {/* Selected Employee Preview */}
              {selectedEmployee && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: '#EEF2FF',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 700, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                  >
                    الموظف المختار:
                  </Typography>
                  <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#4338CA' }}>
                      <AccountCircle />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedEmployee.name}</Typography>
                      {selectedEmployee.employee_id && (
                        <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                          رقم الموظف: {selectedEmployee.employee_id}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              )}

              <TextField
                label="طريقة الدفع"
                name="method"
                value={form.method}
                onChange={(e) => {
                  const displayName = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    method: displayName,
                  }));
                }}
                select
                fullWidth
                sx={fieldSx}
              >
                {paymentTypes.map((pt) => (
                  <MenuItem key={pt.id} value={pt.display_name}>
                    {pt.display_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="تاريخ الدفع"
                name="paymentDate"
                type="date"
                value={form.paymentDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />

              {/* Cheque Details Section */}
              {form.method === 'شيك' && (
                <Stack spacing={2}>
                  <TextField
                    label="المبلغ"
                    name="amount"
                    type="number"
                    value={form.amount}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={fieldSx}
                  />
                  <TextField
                    label="تاريخ استحقاق الشيك"
                    type="date"
                    name="dueDate"
                    value={form.chequeDetails.dueDate}
                    onChange={handleChequeChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  <TextField
                    label="رقم الشيك"
                    name="chequeNumber"
                    value={form.chequeDetails.chequeNumber}
                    onChange={handleChequeChange}
                    fullWidth
                    sx={fieldSx}
                  />
                  <BankBranchSelect
                    fieldSx={fieldSx}
                    bankId={form.chequeDetails.bankId}
                    bankName={form.chequeDetails.bankName}
                    branchId={form.chequeDetails.branchId}
                    branchName={form.chequeDetails.branchName}
                    onChange={(patch) =>
                      setForm((prev) => ({
                        ...prev,
                        chequeDetails: { ...prev.chequeDetails, ...patch },
                      }))
                    }
                  />
                  <TextField
                    label="رقم الحساب"
                    name="accountNumber"
                    value={form.chequeDetails.accountNumber}
                    onChange={handleChequeChange}
                    fullWidth
                    sx={fieldSx}
                  />

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      py: 1.1,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      borderColor: '#4338CA',
                      color: '#4338CA',
                      borderStyle: 'dashed',
                      '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF', borderStyle: 'dashed' },
                    }}
                  >
                    رفع صورة الشيك
                    <input type="file" hidden accept="image/*,.pdf" onChange={handleFileChange} />
                  </Button>
                  {attachment && (
                    <Typography variant="body2" sx={{ color: '#16A34A', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      ملف محدد: {attachment.name}
                    </Typography>
                  )}
                  {!attachment && payment.cheque?.cheque_image_url && (
                    <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      صورة شيك موجودة مسبقاً
                    </Typography>
                  )}
                </Stack>
              )}

              {/* Visa Details Section */}
              {form.method === 'فيزا' && (
                <Stack spacing={2}>
                  <TextField
                    label="المبلغ"
                    name="amount"
                    type="number"
                    value={form.amount}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={fieldSx}
                  />
                  <TextField
                    label="رقم إيصال الفيزا"
                    name="receiptNumber"
                    value={form.visaDetails.receiptNumber}
                    onChange={handleVisaChange}
                    fullWidth
                    sx={fieldSx}
                    placeholder="أدخل رقم إيصال الفيزا"
                  />
                  <TextField
                    label="ملاحظات الفيزا"
                    name="notes"
                    value={form.visaDetails.notes}
                    onChange={handleVisaChange}
                    fullWidth
                    multiline
                    rows={3}
                    sx={fieldSx}
                    placeholder="ملاحظات إضافية للدفع بالفيزا"
                  />
                </Stack>
              )}

              {/* Amount for non-cheque/non-visa */}
              {form.method !== 'شيك' && form.method !== 'فيزا' && (
                <TextField
                  label="المبلغ"
                  name="amount"
                  type="number"
                  value={form.amount}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={fieldSx}
                />
              )}

              <TextField
                label="سبب الصرف"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                fullWidth
                sx={fieldSx}
              />
            </Stack>

            <Stack useFlexGap
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="flex-end"
              mt={4}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate('/financial')}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3,
                  py: 1.1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#DC2626',
                  color: '#DC2626',
                  '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                }}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isUpdating || (form.entityType === 'موظف' && minimalStatus === 'loading')}
                sx={{
                  bgcolor: '#4338CA',
                  color: '#fff',
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 4,
                  py: 1.1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
                }}
              >
                {isUpdating ? 'جاري الحفظ...' : 'حفظ التعديلات'}
              </Button>
            </Stack>
          </MotionPaper>
        </form>
      </Box>
    </Box>
  );
};

export default EditPaymentPage;
